import { useMemo } from 'react';
import {
  useQuery,
  QueryHookOptions,
  QueryResult,
  useLazyQuery,
} from '@apollo/client';
import { DocumentNode } from 'graphql';
import { INodeData } from '../NodeQuery/NodeQuery';
import { TSelectedDetail } from '../../../Types/MasterDetailTypes';
import {
  NewId,
  nextIdParam,
  NO_ID_AVAILABLE,
  prevIdParam,
  selectedId,
} from '../../MasterDetail/MasterDetailUtils';
import { mergeDeepLeft } from 'ramda';

/** Svar på en node fråga */
export interface INodeResultData {
  node: INodeData | null;
}

export interface INodeQueryResult<TData, TNodeData, TVariables>
  extends QueryResult<TData, TVariables> {
  nodeData: TNodeData | null | undefined;
}

export interface ISelectedDetailQueryOptions<TData, TVariables> {
  queryOptions?: QueryHookOptions<TData, TVariables>;
  /**Hämta inte nästa och föregående automatiskt */
  disablePreFetch?: boolean;
  //**Hämta inte data via node-queryn (för att testa visning av preliminiär data) */
  disableNodeQuery?: boolean;
  nextFetchOnCompleted?: (data: INodeResultData) => void;
  prevFetchOnCompleted?: (data: INodeResultData) => void;
}

export const useSelectedDetailQuery: <
  TData extends INodeResultData,
  TNodeData extends INodeData,
  TVariables extends { id: string }
>(
  query: DocumentNode,
  selectedDetail: TSelectedDetail,
  options?: ISelectedDetailQueryOptions<TData, TVariables>
) => INodeQueryResult<TData, TNodeData, TVariables> = (
  query,
  selectedDetail,
  options
) => {
  const currentNodeId = selectedId(selectedDetail);
  const isNew = currentNodeId === NewId;
  const id = isNew ? '' : currentNodeId;

  const [fetchNext] = useLazyQuery<INodeResultData>(query, {
    onCompleted: (data: INodeResultData) => {
      if (options?.nextFetchOnCompleted) {
        options?.nextFetchOnCompleted(data);
      }
    },
  });
  const [fetchPrev] = useLazyQuery<INodeResultData>(query, {
    onCompleted: (data: INodeResultData) => {
      if (options?.prevFetchOnCompleted) {
        options.prevFetchOnCompleted(data);
      }
    },
  });

  const nextId = nextIdParam(selectedDetail);
  const prevId = prevIdParam(selectedDetail);

  const currentIdOptions = useMemo(
    () => ({
      ...options?.queryOptions,
      skip:
        id == null ||
        id === '' ||
        id === NO_ID_AVAILABLE ||
        !!options?.disableNodeQuery ||
        options?.queryOptions?.skip,
      variables: { ...options?.queryOptions?.variables, id: id ?? '' },
      onCompleted: (data: any) => {
        if (!options?.disablePreFetch) {
          if (nextId !== NO_ID_AVAILABLE) {
            fetchNext({
              variables: { ...options?.queryOptions?.variables, id: nextId },
            });
          }
          if (prevId !== NO_ID_AVAILABLE) {
            fetchPrev({
              variables: { ...options?.queryOptions?.variables, id: prevId },
            });
          }
        }
        if (options?.queryOptions?.onCompleted) {
          options?.queryOptions?.onCompleted(data);
        }
      },
    }),
    [
      options?.queryOptions,
      options?.disableNodeQuery,
      options?.disablePreFetch,
      id,
      nextId,
      prevId,
      fetchNext,
      fetchPrev,
    ]
  );
  const useQueryResult = useQuery(
    query,
    currentIdOptions as QueryHookOptions<any, any>
  );

  const nodeData = useQueryResult.data && useQueryResult.data.node;
  const prelData =
    selectedDetail == null || typeof selectedDetail === 'string'
      ? null
      : selectedDetail.data;

  const result = useMemo(
    () => ({
      nodeData:
        nodeData && prelData
          ? mergeDeepLeft(nodeData, prelData as INodeResultData)
          : nodeData ?? prelData,
      ...useQueryResult,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prelData, useQueryResult]
  );

  const empty: any = { loading: false };
  return isNew ? empty : result;
};
